import { BusinessObjectPart, BusinessObjectPartContext, CollectionChangedJoinPoint, FeatureFlagsRulesServiceBase, ModelBase, ObservableCollection, PropertyChangedJoinPoint, PropertyChangingJoinPoint, Weaver } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, ExchangeRateModel, JpkVatCodes, SaleAdvanceDocumentItemModel, SaleDocumentItemModel, SaleDocumentKind, SplitPaymentState, VatSummary } from '@skylab-shared/dataModel';
import { ContextKeys } from '../../common/context-keys';
import { splitPaymentChecked } from '../../common/document-utils';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery, SaleCommunityTransactionSymbol } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { WrongPaymentMethodOnDocumentWithSplitPaymentWarning } from '../../documents/violations/wrong-payment-method-on-document-with-split-payment-warning';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';
import { isAdjustment } from '../../sale/utils/sale-utils';
import { ProductRepositoryContract } from '../../shared-business-temporary-contracts';
import { collectionChangedJoinPointFilter } from '../../utils/collection-changed-filter';
import { propertyChangingJoinPointFilter } from '../../utils/property-changing-filter';
import { JpkGroupsMppLawChangesFlag, SplitPaymentFlag } from '../sale-feature-flags-rules.service-base';
import { MandatorySplitPaymentSinceNowWarning } from '../violations/mandatory-split-payment-since-now-warning';
import { SplitPaymentOnNonDomesticSaleError } from '../violations/split-payment-on-non-domestic-sale-error';
import { SplitPaymentShouldBeCheckedWarning } from '../violations/split-payment-should-be-checked-warning';

type Item = SaleDocumentItemModel & SaleAdvanceDocumentItemModel;

interface DocumentModelPart extends ModelBase {
    communityTransactionId: number;
    splitPayment: SplitPaymentState;
    paymentMethodId: number;
    items: ObservableCollection<Item>;
    kind: SaleDocumentKind;
    advanceValue?: number;
    grossValue: number;
    exchangeRate: ExchangeRateModel;
    taxLiabilityDate?: Date;
    issueDate?: Date;
    vatSummary?: VatSummary;
    issuedByVatPayer: boolean;
    payments?: ObservableCollection<DocumentPaymentListItemModel>;
    jpkVatGroups: string[];
}

export const SplitPaymentLimitInPln = 15_000;

export class SaleDocumentSplitPaymentBoPart extends BusinessObjectPart<DocumentModelPart> {
    private static DocumentWithAllowedSplitPayment = [
        SaleDocumentKind.Invoice,
        SaleDocumentKind.InvoiceAdjustment,
        SaleDocumentKind.AdvanceInvoice,
        SaleDocumentKind.AdvanceInvoiceAdjustment,
        SaleDocumentKind.ProForma
    ];

    public constructor(
        weaver: Weaver,
        private readonly productRepository: ProductRepositoryContract,
        private readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);

        await Promise.all([
            this.bo.localContext.preload(CommunityTransactionForSaleQuery),
            this.bo.localContext.preload(PaymentMethodQuery)
        ]);

        this.runSplitPaymentChecker();

        this.checkIfSplitPaymentShouldBeChecked();
        this.checkIfProperPaymentMethodIsSelected();

        this.registerListening(this.weaver.register(
            PropertyChangingJoinPoint,
            propertyChangingJoinPointFilter(this.bo)(this.modelType, ['splitPayment'])(ctx => {
                const model = this.bo.model;
                if (!SaleDocumentSplitPaymentBoPart.DocumentWithAllowedSplitPayment.includes(model.kind) && ctx.newValue !== SplitPaymentState.False) {
                    ctx.cancel();
                }
            })
        ));
    }

    private async checkSplitPaymentIfNeededAsync(): Promise<void> {
        if (!this.splitPaymentAllowedAndEnabled)
            return;
        if (this.bo.model.vatSummary && splitPaymentChecked(this.bo.model.vatSummary.splitPayment!))
            return;
        const splitPayment = await this.isSplitPaymentObligatoryAsync();
        if (this.bo.model.splitPayment !== SplitPaymentState.Manual) {
            this.bo.model.splitPayment = splitPayment ? SplitPaymentState.True : SplitPaymentState.False;
        }
    }

    private runSplitPaymentChecker(): void {

        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            propertyChangedJoinPointFilter(this.bo)(this.modelType, ['communityTransactionId', 'grossValue', 'taxLiabilityDate', 'advanceValue'])(this.checkSplitPaymentIfNeededAsync.bind(this))
        ));

        this.registerListening(this.weaver.register(
            CollectionChangedJoinPoint,
            collectionChangedJoinPointFilter(this.bo)(this.bo.model.items)(this.checkSplitPaymentIfNeededAsync.bind(this))
        ));

        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            propertyChangedJoinPointFilter(this.bo)(ExchangeRateModel, ['rate', 'units'])(this.checkSplitPaymentIfNeededAsync.bind(this))
        ));
    }

    private checkIfSplitPaymentShouldBeChecked(): void {
        const splitPaymentShouldBeCheckedValidator = async (splitPaymentState: SplitPaymentState) => {
            if (!this.splitPaymentAllowedAndEnabled)
                return true;
            const splitPaymentObligatory = await this.isSplitPaymentObligatoryAsync();
            return (splitPaymentObligatory && splitPaymentChecked(splitPaymentState)) || !splitPaymentObligatory;
        };

        this.engine.validation.addRule(
            [[this.modelType, 'communityTransactionId'], [this.modelType, 'grossValue'], [this.modelType, 'advanceValue'], [this.modelType, 'splitPayment']],
            [[this.modelType, 'splitPayment']],
            ctx => splitPaymentShouldBeCheckedValidator(ctx.bo.model.splitPayment),
            SplitPaymentShouldBeCheckedWarning
        );

        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            propertyChangedJoinPointFilter(this.bo)(ExchangeRateModel, ['rate', 'units'])(() => {
                this.engine.validation.queueValidation([this.modelType, 'splitPayment']);
            })
        ));

        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            propertyChangedJoinPointFilter(this.bo)(this.modelType, ['jpkVatGroups'])(ctx => {
                if (this.splitPaymentAllowedAndEnabled && !this.jpkGroupsMppLawChangesFlagEnabled) {
                    // linksAdjusted - https://skylab.myjetbrains.com/youtrack/issue/SL-5913
                    if (!(this.isSaleInvoiceDocument && ctx.sender.linksAdjusted)) {
                        this.syncSplitPaymentWithJpkGroups();
                    }
                }
            })
        ));

        this.engine.validation.addRule(
            [[this.modelType, 'communityTransactionId'], [this.modelType, 'taxLiabilityDate']],
            [[this.modelType, 'splitPayment']],
            ctx => {
                if (!this.splitPaymentAllowedAndEnabled)
                    return true;
                const { id: ooId } = this.bo.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'OO' })!;
                return !(ctx.source.communityTransactionId === ooId);
            },
            MandatorySplitPaymentSinceNowWarning
        );

        this.engine.validation.addRule(
            [[this.modelType, 'communityTransactionId'], [this.modelType, 'splitPayment']],
            [[this.modelType, 'splitPayment']],
            ctx => {
                if (!this.splitPaymentAllowed)
                    return true;
                const splitPayment = splitPaymentChecked(ctx.source.splitPayment);
                return (splitPayment && this.isDomesticSalesTransaction(this.bo.model.communityTransactionId)) || !splitPayment;
            },
            SplitPaymentOnNonDomesticSaleError
        );
    }

    private syncSplitPaymentWithJpkGroups(): void {
        this.bo.model.splitPayment = this.bo.model.jpkVatGroups.includes(JpkVatCodes.MPP) ? SplitPaymentState.Manual : SplitPaymentState.False;
    }

    private checkIfProperPaymentMethodIsSelected(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'splitPayment'], [this.modelType, 'paymentMethodId']],
            [[this.modelType, 'paymentMethodId']],
            ctx => {
                if (!this.splitPaymentAllowed)
                    return true;
                const bankTransferIds = this.bo.localContext.filterPreloaded(PaymentMethodQuery, {
                    cashPayment: false,
                    associatedWithBankAccount: true
                }).map(({ id }) => id);
                const splitPayment = splitPaymentChecked(ctx.source.splitPayment);
                return (splitPayment && bankTransferIds.includes(ctx.source.paymentMethodId)) || !splitPayment;
            },
            WrongPaymentMethodOnDocumentWithSplitPaymentWarning
        );
    }

    private async isSplitPaymentObligatoryAsync(): Promise<boolean> {
        if (this.isDomesticSalesTransaction(this.bo.model.communityTransactionId) && this.documentChainGrossValue > SplitPaymentLimitInPln) {
            const productsLoading = this.bo.model.items
                .filter(item => typeof item.productId === 'number')
                .map(item => this.bo.localContext.getAsync(ContextKeys.product(item.productId!), () => this.productRepository.findAsync(item.productId!)));
            const products = await Promise.all(productsLoading);
            return products.some(product => product.subjectToReverseCharge);
        }
        return false;
    }

    private isDomesticSalesTransaction(transactionId: number): boolean {
        const availableSymbols: SaleCommunityTransactionSymbol[] = ['SK', 'WSTO-SK'];
        const availableIds = this.bo.localContext.getPreloaded(CommunityTransactionForSaleQuery)!
            .reduce((prev, curr) => availableSymbols.includes(curr.symbol) ? [...prev, curr.id] : prev, [] as number[]);

        return availableIds.includes(transactionId);
    }

    private get splitPaymentAllowedAndEnabled(): boolean {
        return this.splitPaymentAllowed && !!this.splitPaymentFeatureFlagContext && this.isSplitPaymentEnabled(this.splitPaymentFeatureFlagContext);
    }

    private get documentChainGrossValue(): number {
        const model = this.bo.model;
        const exchangeRate = this.calculateExchangeRate(model);
        const documentGrossValue = (model.advanceValue == null ? model.grossValue : model.advanceValue) * exchangeRate;
        const vatSummary = model.vatSummary;
        const countVatSummary = isAdjustment(model.kind) && vatSummary != null;
        return countVatSummary
            ? vatSummary.vatTable.items.reduce((sum, item) => sum + item.grossValue, documentGrossValue)
            : documentGrossValue;
    }

    private isSplitPaymentEnabled(date: Date) {
        return this.featureFlagsRulesService.getFlagStatus(SplitPaymentFlag, date);
    }

    private calculateExchangeRate(model: DocumentModelPart): number {
        const inForeignCurrency = model.exchangeRate !== null;
        return inForeignCurrency
            ? (model.exchangeRate.rate || 1) / (model.exchangeRate.units || 1)
            : 1;
    }

    private isInvoiceIssuedToReceipt(): boolean {
        return this.bo.model.kind === SaleDocumentKind.Invoice &&
            !!this.bo.model.payments && // sprawdza czy to aby na pewno nie SalePattern
            !!this.bo.localContext.getPreloaded<boolean>(ContextKeys.isInvoiceToReceipt(this.bo.model.sun));
    }

    private get isSaleInvoiceDocument(): boolean {
        return !!this.bo.model.payments && (this.bo.model.kind === SaleDocumentKind.Invoice);
    }

    private get splitPaymentAllowed(): boolean {
        return SaleDocumentSplitPaymentBoPart.DocumentWithAllowedSplitPayment.includes(this.bo.model.kind)
            && this.bo.model.issuedByVatPayer && !this.isInvoiceIssuedToReceipt();
    }

    private get splitPaymentFeatureFlagContext(): Date | undefined {
        return this.bo.model.kind === SaleDocumentKind.ProForma
            ? this.bo.model.issueDate
            : this.bo.model.taxLiabilityDate;
    }

    private get jpkGroupsMppLawChangesFlagEnabled(): boolean {
        const date = this.splitPaymentFeatureFlagContext;
        return !date ? false : this.featureFlagsRulesService.getFlagStatus(JpkGroupsMppLawChangesFlag, date);
    }
}
